import { Component, OnInit } from '@angular/core';
import { DateTime } from 'luxon';
import { BidHistoryItem, BidHistoryResponse } from 'src/app/services/utility.service';
import { DashboardDataService } from 'src/app/services/dashboard-data.service';
import { finalize } from 'rxjs/operators';
// 1. Import the LocalStorageService
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Component({
  selector: 'app-bid-history',
  templateUrl: './bid-history.component.html',
  styleUrls: ['./bid-history.component.scss'],
})
export class BidHistoryComponent implements OnInit {

  public masterBids: BidHistoryItem[] = [];
  filteredBids: BidHistoryItem[] = [];

  maxDate: DateTime = DateTime.local().startOf('day');
  selectedDate: DateTime = DateTime.local().startOf('day');
  isLoading = false;
  errorMessage: string | null = null;

  searchTerm: string = '';

  selectedFilter: string = 'All';
  selectedBidId: string | null = null;

  totalItems = 0;
  currentPage = 1;
  limit = 10;
  totalPages = 0;
  pageSizeOptions = [5, 10, 25, 50];

  clientSideMode = false;
  readonly ALL_FETCH_LIMIT = 10000;

  private bidTypeMap: Record<string, string> = {
    'Day ahead': 'daily',
    'Monthly': 'monthly',
    'Preferential Exchange of Energy': 'preferential',
    'Preferential': 'preferential'
  };

  constructor(
    private dashboardData: DashboardDataService,

    private localStorageService: LocalStorageService
  ) {}

  ngOnInit() {
    this.selectedDate = DateTime.local().startOf('day');
    this.maxDate = this.selectedDate;
    this.fetchBids();
  }

  fetchBids(): void {
    this.isLoading = true;
    this.errorMessage = null;


    const utilityId = this.localStorageService.getUserId();


    if (!utilityId && !this.dashboardData.isPartnerSession()) {
      this.isLoading = false;
      this.errorMessage = 'Utility ID not found. Cannot load bid history.';
      this.masterBids = [];
      this.filteredBids = [];
      this.totalItems = 0;
      this.totalPages = 0;
      return;
    }

    if (this.selectedFilter && this.selectedFilter.startsWith('bidType:')) {
      this.clientSideMode = false;
      this.fetchServerPage(utilityId);
      return;
    }

    if (this.selectedFilter && this.selectedFilter.startsWith('userType:')) {
      this.clientSideMode = true;
      this.fetchAllAndFilterClientSide(utilityId);
      return;
    }

    this.clientSideMode = false;
    this.fetchServerPage(utilityId);
  }

  private fetchServerPage(utilityId: string) {
    const offset = (this.currentPage - 1) * this.limit;

    let bidTypeToSend: string | undefined;
    if (this.selectedFilter && this.selectedFilter.startsWith('bidType:')) {
      const rawLabel = this.selectedFilter.split(':')[1] || '';
      const mapped = this.bidTypeMap[rawLabel];
      bidTypeToSend = mapped ?? rawLabel?.toLowerCase();
    }

    this.dashboardData.getBidHistory(
      this.selectedDate.toISODate() ?? '',
      this.selectedDate.toISODate() ?? '',
      this.limit,
      offset,
      bidTypeToSend
    ).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (response: BidHistoryResponse) => {
          this.masterBids = response.items || [];
          this.totalItems = response.total_items ?? (this.masterBids.length || 0);
          if (response.current_page && response.current_page > 0) {
            this.currentPage = response.current_page;
          }
          this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.limit));
          this.applyFilters();
        },
        error: (err) => {
          console.error('Failed to fetch bid history (server page):', err);
          this.errorMessage = 'Could not load bid history. Please try again later.';
          this.masterBids = [];
          this.filteredBids = [];
          this.totalItems = 0;
          this.totalPages = 0;
        }
      });
  }

  private fetchAllAndFilterClientSide(utilityId: string) {
    const offset = 0;
    const limitAll = this.ALL_FETCH_LIMIT;

    this.dashboardData.getBidHistory(
      this.selectedDate.toISODate() ?? '',
      this.selectedDate.toISODate() ?? '',
      limitAll,
      offset
    ).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (response: BidHistoryResponse) => {
          const allItems = response.items || [];
          const userTypeValue = (this.selectedFilter.split(':')[1] || '').trim();
          const filteredFull = allItems.filter(item => {
            return item.user_type && item.user_type.toLowerCase() === userTypeValue.toLowerCase();
          });
          this.masterBids = filteredFull;
          this.totalItems = filteredFull.length;
          this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.limit));
          if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages;
          }
          if (this.currentPage < 1) {
            this.currentPage = 1;
          }
          this.applyFilters();
        },
        error: (err) => {
          console.error('Failed to fetch bid history (all items):', err);
          this.errorMessage = 'Could not load bid history. Please try again later.';
          this.masterBids = [];
          this.filteredBids = [];
          this.totalItems = 0;
          this.totalPages = 0;
        }
      });
  }

  applyFilters(): void {
    let bids = [...this.masterBids];
    const term = this.searchTerm?.trim().toLowerCase();
    if (term) {
      bids = bids.filter(bid => (bid.participant || '').toLowerCase().includes(term));
    }
    if (!this.clientSideMode && this.selectedFilter && this.selectedFilter.startsWith('userType:')) {
      const userTypeValue = (this.selectedFilter.split(':')[1] || '').trim();
      bids = bids.filter(bid => bid.user_type && bid.user_type.toLowerCase() === userTypeValue.toLowerCase());
    }
    if (this.clientSideMode) {
      const totalFiltered = bids.length;
      this.totalItems = totalFiltered;
      this.totalPages = Math.max(1, Math.ceil(totalFiltered / this.limit));
      if (this.currentPage > this.totalPages) this.currentPage = this.totalPages;
      if (this.currentPage < 1) this.currentPage = 1;
      const start = (this.currentPage - 1) * this.limit;
      const end = this.currentPage * this.limit;
      this.filteredBids = bids.slice(start, end);
    } else {
      this.filteredBids = bids;
    }
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.applyFilters();
  }

  onCombinedFilterChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedFilter = select.value;
    this.currentPage = 1;
    this.fetchBids();
  }

  onDateChange(ev: any): void {
    if (ev.value) {
      this.selectedDate = DateTime.fromJSDate(ev.value).startOf('day');
      this.currentPage = 1;
      this.selectedBidId = null; // Clear selection on date change
      this.fetchBids();
    }
  }

  selectBid(id: string): void {
    this.selectedBidId = this.selectedBidId === id ? null : id;
  }

  changeDay(amount: number): void {
    this.selectedDate = this.selectedDate.plus({ days: amount });
    this.currentPage = 1;
    this.fetchBids();
  }

  goToPage(page: number): void {
    if (page < 1) page = 1;
    if (page > this.totalPages) page = this.totalPages;
    if (page === this.currentPage) return;
    this.currentPage = page;
    if (this.clientSideMode) {
      this.applyFilters();
    } else {
      this.fetchBids();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  changeLimit(newLimit: number | string): void {
    const parsed = typeof newLimit === 'string' ? Number(newLimit) : newLimit;
    if (!parsed || parsed <= 0) return;
    this.limit = parsed;
    this.currentPage = 1;
    if (this.clientSideMode) {
      this.applyFilters();
    } else {
      this.fetchBids();
    }
  }

  visibleRange(): { from: number; to: number } {
    const from = this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.limit + 1;
    const to = Math.min(this.totalItems, this.currentPage * this.limit);
    return { from, to };
  }

  shouldShowPageButton(page: number): boolean {
    const total = this.totalPages;
    const cur = this.currentPage;
    if (total <= 7) return true;
    if (page <= 2 || page > total - 2) return true;
    if (Math.abs(page - cur) <= 1) return true;
    return false;
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }
}